import React, { useEffect, useState } from 'react';
import InvoiceLink from './InvoiceLink';
import { BedDouble, MapPin, Star, Wallet } from 'lucide-react';
import apiService from '../apiService';
import { ratingText } from './ratingText';

/**
 * Hébergements web — liste, détail (types de chambres, dates, voyageurs),
 * réservation (solde Mbiyo, ou paiement sur place si l'hébergement l'autorise —
 * le serveur calcule et fait foi pour les montants), mes réservations + annulation.
 */
const nightsBetween = (a, b) => Math.max(1, Math.round((new Date(b) - new Date(a)) / 86400000));
const d2s = (d) => d.toISOString().split('T')[0];

export function LodgingsList({ nav, citySlug, supplierId }) {
  const [list, setList] = useState(null);
  // Recherche façon Booking : dates + voyageurs + chambres ; avec des dates, seuls les
  // hébergements réservables sont renvoyés (disponibilité réelle), avec le prix du séjour.
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [rooms, setRooms] = useState(1);
  const hasStay = checkIn && checkOut && checkOut > checkIn;
  const nights = hasStay ? nightsBetween(checkIn, checkOut) : 0;
  useEffect(() => {
    const qs = new URLSearchParams({ limit: 40 });
    if (supplierId) qs.set('supplier_id', supplierId); // hébergements d'un hôtel précis (lien direct /b/<id>)
    else if (citySlug) qs.set('city_slug', citySlug);
    if (!supplierId && hasStay) { qs.set('check_in', checkIn); qs.set('check_out', checkOut); qs.set('guests', adults + children); qs.set('rooms', rooms); }
    setList(null);
    // Filtre hôtel : annonces publiées seulement.
    apiService.get(`/lodgings?${qs}`).then(r => setList((r?.lodgings || []).filter(l => !supplierId || (l.status === 'active' && l.is_active !== false)))).catch(() => setList([]));
  }, [citySlug, supplierId, checkIn, checkOut, adults, children, rooms, hasStay]);

  return (
    <div className="mk-container">
      {!supplierId && (
        <div className="mk-panel" style={{ marginTop: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            <div className="mk-field"><label>Arrivée</label><input type="date" value={checkIn} min={d2s(new Date())} onChange={e => setCheckIn(e.target.value)} /></div>
            <div className="mk-field"><label>Départ</label><input type="date" value={checkOut} min={checkIn || d2s(new Date())} onChange={e => setCheckOut(e.target.value)} /></div>
            <div className="mk-field"><label>Adultes</label><input type="number" min="1" value={adults} onChange={e => setAdults(Math.max(1, +e.target.value || 1))} /></div>
            <div className="mk-field"><label>Enfants</label><input type="number" min="0" value={children} onChange={e => setChildren(Math.max(0, +e.target.value || 0))} /></div>
            <div className="mk-field"><label>Chambres</label><input type="number" min="1" value={rooms} onChange={e => setRooms(Math.max(1, +e.target.value || 1))} /></div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
            <p className="mk-note" style={{ margin: 0 }}>{hasStay ? `${nights} nuit(s) · seuls les hébergements disponibles sont affichés` : 'Choisissez vos dates pour voir les disponibilités et le prix du séjour.'}</p>
            <button className="mk-btn ghost" onClick={() => nav({ page: 'lodging-bookings' })}><BedDouble size={16} /> Mes réservations</button>
          </div>
        </div>
      )}
      {list === null && <div className="mk-center"><div className="mk-spin" /> Chargement…</div>}
      {list !== null && !list.length && <div className="mk-center"><BedDouble size={42} /> {hasStay ? 'Rien de disponible à ces dates (changez de dates, de ville ou de voyageurs).' : 'Aucun hébergement disponible pour le moment.'}</div>}
      <div className="mk-section" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
        {(list || []).map(l => (
          <button key={l.lodging_id} className="mk-card" onClick={() => nav({ page: 'lodging', lodgingId: l.lodging_id, ...(hasStay ? { checkIn, checkOut, adults, children } : {}) })}>
            <div className="mk-card-imgwrap">
              {l.cover_image ? <img className="mk-card-img" style={{ height: 150 }} src={l.cover_image} alt="" loading="lazy" /> : <div className="mk-card-img mk-card-ph" style={{ height: 150 }}>🏨</div>}
            </div>
            <span className="mk-card-title">{l.name}</span>
            <span className="mk-meta"><MapPin size={11} /> {l.address || l.city_slug}{l.review_count > 0 ? <> · <Star size={11} fill="#FFB900" color="#FFB900" /> {ratingText(l.average_rating, l.review_count)}</> : null}</span>
            {l.stay?.available
              ? <span className="mk-card-price">{nights} nuit(s) · {adults + children} pers.{rooms > 1 ? ` · ${rooms} ch.` : ''} — <b>{(l.stay.price_for_stay || 0).toLocaleString('fr-FR')} FC</b></span>
              : <span className="mk-card-price">dès {(l.price_per_night || l.pricing?.price_per_night || l.room_types?.[0]?.price_per_night || l.min_price || 0).toLocaleString('fr-FR')} FC/nuit</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export function LodgingDetail({ lodgingId, nav, initialCheckIn, initialCheckOut, initialAdults, initialChildren }) {
  const [lodging, setLodging] = useState(null);
  const [room, setRoom] = useState(null);
  const today = new Date(); const tomorrow = new Date(Date.now() + 86400000);
  const [checkIn, setCheckIn] = useState(initialCheckIn || d2s(today));
  const [checkOut, setCheckOut] = useState(initialCheckOut || d2s(tomorrow));
  const [adults, setAdults] = useState(Number(initialAdults) || 2);
  const [children, setChildren] = useState(Number(initialChildren) || 0);
  const [pay, setPay] = useState('balance');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.get(`/lodgings/${lodgingId}`).then(r => {
      const l = r?.lodging || r?.data?.lodging;
      if (l) { setLodging(l); setRoom((l.room_types || [])[0] || null); }
    }).catch(() => {});
  }, [lodgingId]);

  if (!lodging) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;

  const nights = nightsBetween(checkIn, checkOut);
  const pricePerNight = room?.price_per_night ?? lodging.price_per_night ?? 0;
  const estimated = nights * pricePerNight;
  const onSiteAllowed = lodging.payment_options === 'on_site_allowed';

  const book = async () => {
    setError(null); setBusy(true);
    try {
      const res = await apiService.post('/lodging-bookings', {
        lodging_id: lodging.lodging_id,
        room_type_id: room?.room_type_id || null,
        check_in_date: checkIn, check_out_date: checkOut,
        guests: { adults, children },
        payment_method: pay, client_note: note.trim() || null,
      });
      const booking = res?.booking || res?.data?.booking;
      if (!res?.success || !booking) throw new Error(res?.error || 'Réservation impossible');
      nav({ page: 'lodging-bookings' });
    } catch (e) { setError(e?.error || e?.message || 'Erreur'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mk-container">
      <div className="mk-two">
        <div className="mk-panel" style={{ padding: 0, overflow: 'hidden' }}>
          {lodging.cover_image && <img src={lodging.cover_image} alt="" style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block' }} />}
          <div style={{ padding: 18 }}>
            <h3 style={{ fontSize: '1.3rem' }}>{lodging.name}</h3>
            <p className="mk-note"><MapPin size={13} /> {lodging.address}{lodging.check_in_time ? ` · Arrivée dès ${lodging.check_in_time}` : ''}</p>
            {lodging.description && <p className="mk-desc" style={{ marginTop: 10 }}>{lodging.description}</p>}
            {(lodging.room_types || []).length > 0 && (
              <>
                <h4 style={{ margin: '14px 0 6px' }}>Chambres</h4>
                {(lodging.room_types || []).map(rt => (
                  <button key={rt.room_type_id} className={`mk-opt${room?.room_type_id === rt.room_type_id ? ' on' : ''}`} onClick={() => setRoom(rt)}>
                    <span className="box">{room?.room_type_id === rt.room_type_id ? '✓' : ''}</span>
                    <span className="name">{rt.name}{rt.capacity ? ` · ${rt.capacity} pers.` : ''}</span>
                    <span className="delta">{(rt.price_per_night || 0).toLocaleString('fr-FR')} FC/nuit</span>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="mk-panel">
          <h3>Réserver</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="mk-field"><label>Arrivée</label><input type="date" value={checkIn} min={d2s(today)} onChange={e => setCheckIn(e.target.value)} /></div>
            <div className="mk-field"><label>Départ</label><input type="date" value={checkOut} min={checkIn} onChange={e => setCheckOut(e.target.value)} /></div>
            <div className="mk-field"><label>Adultes</label><input type="number" min="1" value={adults} onChange={e => setAdults(Math.max(1, +e.target.value || 1))} /></div>
            <div className="mk-field"><label>Enfants</label><input type="number" min="0" value={children} onChange={e => setChildren(Math.max(0, +e.target.value || 0))} /></div>
          </div>
          <div className="mk-pay">
            <button className={pay === 'balance' ? 'on' : ''} onClick={() => setPay('balance')}><Wallet size={18} />Solde Mbiyo</button>
            {onSiteAllowed && <button className={pay === 'on_site' ? 'on' : ''} onClick={() => setPay('on_site')}>🏨 Sur place</button>}
          </div>
          <div className="mk-field" style={{ marginTop: 10 }}><label>Note (facultatif)</label>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Ex. arrivée tardive" /></div>
          <div className="mk-total"><span>{nights} nuit(s) × {pricePerNight.toLocaleString('fr-FR')} FC</span><span>{estimated.toLocaleString('fr-FR')} FC</span></div>
          <p className="mk-note">Le montant exact (taxes/frais éventuels) est calculé par le serveur à la réservation.</p>
          {error && <p className="mk-note err" style={{ marginTop: 8 }}>{error}</p>}
          <button className="mk-btn primary block" style={{ marginTop: 12 }} disabled={busy} onClick={book}>
            {busy ? 'Réservation…' : 'Réserver'}
          </button>
        </div>
      </div>
    </div>
  );
}

const BSTATUS = {
  pending: ['En attente', '#FFB900'], confirmed: ['Confirmée', '#10B981'], checked_in: ['Séjour en cours', '#FF7800'],
  checked_out: ['Terminée', '#5C636E'], completed: ['Terminée', '#5C636E'], cancelled: ['Annulée', '#DC2626'], no_show: ['Non présenté', '#DC2626'],
};

export function LodgingBookings({ nav }) {
  const [bookings, setBookings] = useState(null);
  const load = () => apiService.get('/lodging-bookings?role=client').then(r => setBookings(r?.bookings || [])).catch(() => setBookings([]));
  useEffect(() => { load(); }, []);

  const cancel = async (id) => {
    if (!window.confirm('Annuler cette réservation ?')) return;
    const r = await apiService.patch(`/lodging-bookings/${id}/cancel`, {}).catch(e => e);
    if (!r?.success) alert(r?.error || r?.message || 'Annulation impossible');
    load();
  };

  if (bookings === null) return <div className="mk-center"><div className="mk-spin" /> Chargement…</div>;
  return (
    <div className="mk-container">
      <div className="mk-panel" style={{ marginTop: 16 }}>
        <h3>🏨 Mes réservations d'hébergement</h3>
        {!bookings.length && <div className="mk-center">Aucune réservation. <button className="mk-btn primary" onClick={() => nav({ page: 'lodgings' })}>Voir les hébergements</button></div>}
        {bookings.map(b => {
          const [lbl, color] = BSTATUS[b.status] || [b.status, '#5C636E'];
          return (
            <div className="mk-line" key={b.booking_id}>
              <div className="info">
                <div className="name">{b.lodging?.name || b.lodging_name || 'Hébergement'}</div>
                <div className="opt">{b.check_in_date} → {b.check_out_date} · {(b.total_amount || 0).toLocaleString('fr-FR')} FC</div>
                {b.status === 'confirmed' && b.confirmation_code && <div className="opt">Code : <b>{b.confirmation_code}</b></div>}
              </div>
              <span className="mk-open" style={{ background: `${color}1c`, color }}>{lbl}</span>
              <InvoiceLink type="lodging" id={b.booking_id} compact />
              {['pending', 'confirmed'].includes(b.status) && (
                <button className="mk-iconbtn" title="Annuler" onClick={() => cancel(b.booking_id)} style={{ color: 'var(--mb-danger)' }}>✕</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
